import moment from 'moment-timezone';

/**
 * The summary above the Agent Metrics grid: four headline numbers for the range, each
 * against the same length of time just before it, and the activity per day.
 *
 * Only counts that add up across agents and days are summed here. A median does not
 * combine (the backend sends the range's own median in `meta.totals`), and the
 * distinct-conversation counts (answered, closed) would count a conversation once per
 * agent and per day, so neither is built from rows.
 */

/**
 * Daily activity lines, in the categorical order of the chart palette (validated
 * colorblind-safe as a set against the white card; aqua is below 3:1, so the legend and
 * the grid carry every value as well).
 */
export const TREND_SERIES = [
    { key: 'conversations_opened_count', label: 'Conversations opened', color: '#2a78d6' },
    { key: 'reply_to_guest_count', label: 'Replies to guests', color: '#eb6834' },
    { key: 'action_not_taken_after_opening_count', label: 'No action taken', color: '#1baf7a' },
];

const DAY = 'YYYY-MM-DD';

const toDay = (value, tz) => moment.tz(value, DAY, tz).startOf('day');

/** Sum of the additive counts over report rows, plus the range's median from the server. */
export function summarize(rows, medianSeconds = null) {
    const sum = (key) => (rows || []).reduce((n, r) => n + (Number(r?.[key]) || 0), 0);
    const opened = sum('conversations_opened_count');
    const noAction = sum('action_not_taken_after_opening_count');
    return {
        opened,
        replies: sum('reply_to_guest_count'),
        noAction,
        noActionShare: opened > 0 ? noAction / opened : null,
        medianSeconds: medianSeconds ?? null,
    };
}

/**
 * The range a report with these date filters covers, in calendar days of `tz`: the first
 * day, the last, how many, and whether it runs up to now (no end date, or an end date of
 * today or later, which the backend cuts at the moment of the request).
 */
export function reportRange(start, end, tz, now = moment()) {
    if (!start) {
        return null;
    }
    const zone = tz || 'UTC';
    const today = moment(now).tz(zone).startOf('day');
    const first = toDay(start, zone);
    const picked = end ? toDay(end, zone) : today;
    const toNow = !picked.isBefore(today);
    const last = toNow ? today : picked;
    const days = last.diff(first, 'days') + 1;
    if (!first.isValid() || days < 1) {
        return null;
    }
    return { first, last, days, toNow };
}

/**
 * The comparison range: as many days, ending the day before the range starts. When the
 * range runs up to now, the comparison stops at the same time of day, so a morning look at
 * "Last 7 days" does not set a part-day against a whole one.
 *
 * The report endpoint reads `end_datetime` as an inclusive end and adds 24 hours to it
 * (`staff_report`: a picked date covers that whole local day). A cut-off instant is
 * therefore sent 24 hours early, as a UTC ISO string, which the backend keeps as given.
 */
export function previousPeriod(start, end, tz, now = moment()) {
    const range = reportRange(start, end, tz, now);
    if (!range) {
        return null;
    }
    const zone = tz || 'UTC';
    const first = range.first.clone().subtract(range.days, 'days');
    const lastDay = range.first.clone().subtract(1, 'days');
    const period = {
        days: range.days,
        first,
        lastDay,
        start: first.format(DAY),
        end: lastDay.format(DAY),
        toNow: range.toNow,
        cutoff: null,
    };
    if (range.toNow) {
        period.cutoff = moment(now).tz(zone).subtract(range.days, 'days');
        period.end = period.cutoff.clone().subtract(24, 'hours').utc().toISOString();
    }
    return period;
}

/**
 * "Oct 14 – 21", "Sep 28 – Oct 4", "Dec 29, 2025 – Jan 4, 2026", or one day "Oct 14".
 * `compact` writes years as "Dec 29 '25" for the tiles, where the line is short.
 */
export function formatDayRange(first, last, now = moment(), { compact = false } = {}) {
    const sameYear = first.year() === last.year();
    const showYear = !sameYear || first.year() !== moment(now).year();
    const withYear = compact ? "MMM D [']YY" : 'MMM D, YYYY';
    if (first.isSame(last, 'day')) {
        return first.format(showYear ? withYear : 'MMM D');
    }
    if (sameYear && first.month() === last.month()) {
        return `${first.format('MMM D')} – ${last.format(showYear ? withYear.replace('MMM ', '') : 'D')}`;
    }
    const fmt = showYear ? withYear : 'MMM D';
    return `${first.format(fmt)} – ${last.format(fmt)}`;
}

/**
 * One point per calendar day of the range, each holding the sum of TREND_SERIES over the
 * by-day rows of that day; a day nobody worked is all zeros rather than a gap. `partial`
 * marks today while the range runs up to now.
 */
export function dailyTotals(rows, start, end, tz, now = moment()) {
    const range = reportRange(start, end, tz, now);
    if (!range) {
        return [];
    }
    const byDay = new Map();
    for (let d = range.first.clone(); !d.isAfter(range.last, 'day'); d.add(1, 'days')) {
        const point = { day: d.format(DAY), partial: range.toNow && d.isSame(range.last, 'day') };
        TREND_SERIES.forEach((s) => { point[s.key] = 0; });
        byDay.set(point.day, point);
    }
    (rows || []).forEach((row) => {
        const point = byDay.get(row?.report_day);
        if (point) {
            TREND_SERIES.forEach((s) => { point[s.key] += Number(row[s.key]) || 0; });
        }
    });
    return Array.from(byDay.values());
}

/**
 * The measures the agent comparison ranks by, the four of the tiles. Counts put the most
 * first; the median puts the fastest first and only ranks agents with enough replies for it
 * to mean something (one quick reply would otherwise top the list).
 */
export const MEDIAN_MIN_REPLIES = 5;
export const COMPARE_METRICS = [
    { key: 'conversations_opened_count', label: 'Conversations opened', order: 'desc' },
    { key: 'reply_to_guest_count', label: 'Replies to guests', order: 'desc' },
    { key: 'median_response_seconds', label: 'Median response time', order: 'asc', duration: true },
    { key: 'action_not_taken_after_opening_count', label: 'No action taken', order: 'desc' },
];

/** The measure the comparison opens on until the viewer picks another (2026-10-29). */
export const DEFAULT_COMPARE_METRIC = 'reply_to_guest_count';

/**
 * The agents to show for a measure, best first, from the grid's rows: `{ userId, name, value,
 * replies }`, at most `limit`, plus how many agents had a value at all. By-day rows are summed per
 * agent first; a median does not add up across days, so by day it has nothing to rank.
 */
export function rankAgents(rows, metricKey, { limit = 10, byDay = false } = {}) {
    const metric = COMPARE_METRICS.find((m) => m.key === metricKey) || COMPARE_METRICS[0];
    const byAgent = new Map();
    (rows || []).forEach((row) => {
        if (!row || row.user_id === undefined || row.user_id === null) return;
        const agent = byAgent.get(row.user_id) || { userId: row.user_id, name: row.user_display_name || `User ${row.user_id}`, value: 0, replies: 0, median: null };
        agent.replies += Number(row.reply_to_guest_count) || 0;
        if (!metric.duration) {
            agent.value += Number(row[metric.key]) || 0;
        } else if (!byDay) {
            agent.median = row.median_response_seconds ?? null;
        }
        byAgent.set(row.user_id, agent);
    });
    let agents = Array.from(byAgent.values());
    if (metric.duration) {
        agents = byDay ? [] : agents
            .filter((a) => a.median !== null && a.replies >= MEDIAN_MIN_REPLIES)
            .map((a) => ({ ...a, value: a.median }));
    } else {
        agents = agents.filter((a) => a.value > 0);
    }
    agents.sort((a, b) => (metric.order === 'asc' ? a.value - b.value : b.value - a.value) || a.name.localeCompare(b.name));
    return {
        metric,
        total: agents.length,
        agents: agents.slice(0, limit).map(({ userId, name, value, replies }) => ({ userId, name, value, replies })),
    };
}

/** Relative change, or null when there is nothing to compare against. */
export function percentChange(current, previous) {
    if (current === null || current === undefined || previous === null || previous === undefined) {
        return null;
    }
    if (previous === 0) {
        return null;
    }
    return (current - previous) / previous;
}

/** "45 s", "8.5 min", "24 min", "1 h 5 min", "2 d 3 h". */
export function formatDuration(seconds) {
    if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
        return '—';
    }
    const s = Math.max(0, Number(seconds));
    if (s < 60) {
        return `${Math.round(s)} s`;
    }
    const minutes = s / 60;
    if (minutes < 10) {
        return `${(Math.round(minutes * 10) / 10).toString()} min`;
    }
    if (minutes < 60) {
        return `${Math.round(minutes)} min`;
    }
    const totalMinutes = Math.round(minutes);
    const hours = Math.floor(totalMinutes / 60);
    if (hours < 48) {
        const rest = totalMinutes % 60;
        return rest ? `${hours} h ${rest} min` : `${hours} h`;
    }
    const days = Math.floor(hours / 24);
    const restHours = hours % 24;
    return restHours ? `${days} d ${restHours} h` : `${days} d`;
}

export const formatCount = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US'));

/**
 * Four or five clean y-axis ticks from 0 past `max`, stepping 1, 2 or 5 times a power of
 * ten: 0 / 50 / 100 / 150 / 200 rather than whatever the chart library lands on.
 */
export function niceTicks(max) {
    if (!(max > 0)) {
        return [0, 1];
    }
    const raw = max / 4;
    const power = 10 ** Math.floor(Math.log10(raw));
    // Counts are whole numbers, so never a step below 1.
    const step = Math.max(1, [1, 2, 5, 10].map((m) => m * power).find((s) => s >= raw));
    const top = Math.ceil(max / step) * step;
    const ticks = [];
    for (let t = 0; t <= top + step / 2; t += step) {
        ticks.push(Math.round(t));
    }
    return ticks.length > 1 ? ticks : [0, step];
}

/** Axis ticks: 950, 1.2K, 12K. */
export function formatCompact(n) {
    const v = Number(n) || 0;
    if (Math.abs(v) < 1000) {
        return String(v);
    }
    const k = v / 1000;
    return `${Math.abs(k) < 10 ? Math.round(k * 10) / 10 : Math.round(k)}K`;
}
